import { Saga, SagaNavbar, SagaPage, SagaSidebar } from "@einride/saga-core"
import { PrimaryButton, Title1, Title3, VerticalSpacing } from "@einride/ui"
import styled from "@emotion/styled"
import { ERROR_PERMISSION_DENIED } from "lib/api/context/errorCodes"
import { useLogger } from "lib/logging"
import { useEffect } from "react"
import { FallbackProps } from "react-error-boundary"
import { useTranslation } from "react-i18next"

const Content = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ErrorMessage = ({ error }: { error: Error | string }): React.JSX.Element => {
  const { t } = useTranslation()
  const err = typeof error === "string" ? error : error.name
  switch (err) {
    case ERROR_PERMISSION_DENIED:
      return (
        <>
          <Title1>{t("errorPage.permissionErrorTitle")}</Title1>
          <Title3>{t("errorPage.permissionErrorDescription")}</Title3>
        </>
      )
    default:
      return (
        <>
          <Title1>{t("errorPage.unexpectedErrorTitle")}</Title1>
          <Title3>{t("errorPage.unexpectedErrorDescription")}</Title3>
        </>
      )
  }
}

export const ErrorPage = ({ resetErrorBoundary, error }: FallbackProps): React.JSX.Element => {
  const { t } = useTranslation()
  const header = document.getElementsByTagName("header")
  const logger = useLogger()

  useEffect(() => {
    logger.error(error)
  }, [error, logger])

  return (
    <StyledSaga navbar={<SagaNavbar />} sidebar={<SagaSidebar />}>
      <StyledSagaPage headerHeight={header[0]?.clientHeight}>
        <Content>
          <ErrorMessage error={error} />
          <VerticalSpacing size="lg" />
          <PrimaryButton onClick={resetErrorBoundary}>{t("errorPage.returnButton")}</PrimaryButton>
        </Content>
      </StyledSagaPage>
    </StyledSaga>
  )
}

export const StyledSaga = styled(Saga)`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  @media ${({ theme }) => theme.mediaQueries.lg} {
    height: 100vh;
  }
`

export const StyledSagaPage = styled(SagaPage)<{ headerHeight?: number }>`
  // fallback to 96px if the header is not found
  height: calc(100vh - ${({ headerHeight }) => headerHeight ?? 96}px);
`
